import { Capacitor } from '@capacitor/core';
import { doc, getDoc, getDocFromServer } from 'firebase/firestore';

import CashfreePaymentBridge from '../../components/CashfreePaymentBridge';
import { db } from '../../firebase';
import { pilotSyncCashfreeOrder } from './payments';

const LOG = '[V2Pay:nativeCashfree]';
const PENDING_ORDER_KEY = 'v2_cashfree_pending_order';

function sleep(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

export function persistPendingCashfreeOrder(orderId) {
  try {
    sessionStorage.setItem(PENDING_ORDER_KEY, String(orderId));
  } catch (e) {
    console.warn(LOG, 'persist_pending_failed', e);
  }
}

export function clearPendingCashfreeOrder() {
  try {
    sessionStorage.removeItem(PENDING_ORDER_KEY);
  } catch (e) {
    console.warn(LOG, 'clear_pending_failed', e);
  }
}

export function readPendingCashfreeOrder() {
  try {
    const v = sessionStorage.getItem(PENDING_ORDER_KEY);
    return v ? String(v).trim() : null;
  } catch {
    return null;
  }
}

/** Close the native Cashfree modal (iOS PaymentViewController). */
export async function dismissNativeCashfreeModal({ resolveAsSuccess = false } = {}) {
  if (!Capacitor.isNativePlatform()) return;
  try {
    await CashfreePaymentBridge.dismissPayment({
      resolveStatus: resolveAsSuccess ? 'SUCCESS' : 'NONE',
    });
  } catch (e) {
    console.warn(LOG, 'dismiss_failed', e);
  }
}

/** Read current txn status from Firestore (prefer server, not stale cache). */
export async function fetchTransactionPaymentStatus(orderId, { fromServer = true } = {}) {
  const ref = doc(db, 'transactions', String(orderId));
  const snap = fromServer ? await getDocFromServer(ref).catch(() => getDoc(ref)) : await getDoc(ref);
  const data = snap.data();
  return {
    status: data?.status ?? null,
    pilotOrderId: data?.pilotOrderId != null ? String(data.pilotOrderId).trim() : '',
  };
}

/**
 * After returning from a UPI app on iOS: sync with Pilot/Cashfree, poll Firestore with retries.
 */
export async function reconcileNativePaymentAfterResume(orderId, { maxAttempts = 12, delayMs = 2000 } = {}) {
  const id = String(orderId).trim();

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    let txn = await fetchTransactionPaymentStatus(id, { fromServer: true });
    if (txn.status === 'PAYMENT_SUCCESS' || txn.status === 'PAYMENT_FAILED') {
      return txn;
    }

    try {
      const synced = await pilotSyncCashfreeOrder(id);
      if (synced.status === 'PAYMENT_SUCCESS') {
        return {
          status: 'PAYMENT_SUCCESS',
          pilotOrderId:
            synced.pilotOrderId != null ? String(synced.pilotOrderId).trim() : '',
        };
      }
      if (synced.status === 'PAYMENT_FAILED') {
        return { status: 'PAYMENT_FAILED', pilotOrderId: '' };
      }
    } catch (e) {
      console.warn(LOG, 'sync_attempt_warn', { attempt: attempt + 1, error: e });
    }

    txn = await fetchTransactionPaymentStatus(id, { fromServer: true });
    if (txn.status === 'PAYMENT_SUCCESS' || txn.status === 'PAYMENT_FAILED') {
      return txn;
    }

    if (attempt < maxAttempts - 1) {
      // eslint-disable-next-line no-await-in-loop
      await sleep(delayMs);
    }
  }

  return fetchTransactionPaymentStatus(id, { fromServer: true });
}
